'use client'

/**
 * Instant Analysis hero — the free Value Map "See your number", the aha-before-the-ask (landing hero).
 * One sentence in → a real computed STATUS (score + ranges + top driver) + the read/assumed review chips +
 * a headroom hook + a needs-matched, installment-forward CTA to the matched paid tier. Public/anonymous.
 * Free = Single Goal & Status; the CTA points at Multiple Goals & Action Plan.
 */

import { useEffect, useRef, useState } from 'react'
import { MC, SERIF, SANS, bandColors } from '@/lib/mycio/theme'
import { useMyCIOLanguage } from '@/lib/mycio/MyCIOLanguageContext'
import { toTraditionalDeep } from '@/lib/mycio/zhHant'
import Link from 'next/link'
import { getMyCIOCopy, getMyCIOInstantHeroCopy, getMyCIOInstantChips, getMyCIOYourDataCopy, type MyCIOInstantHeroCopy } from '@/lib/mycio/translations'
import { track } from '@/lib/analytics/mycio'
import { buildShareUrl, shareOrFallback, trackInviteeMilestone, getDeviceId } from '@/lib/mycio/referral'
import { getMyCIOReferralShareCopy, type MyCIOReferralShareCopy } from '@/lib/mycio/copy/referralShare'
import { formatNumber, formatPercentPoints, isEnglishNumberLocale, wrapCurrency } from '@/lib/mycio/numberFormat'
import { currencyAffixes, exactCurrencyCode, isMajorUnitCode } from '@/lib/mycio/currencyCode'

// "See a sample plan first" — the zero-data taste. A CustomEvent<string> with a fictional-household sentence
// prefills the input and runs it (dispatched by the landing's YourDataSection; also honoured via ?try=sample
// so /mycio/your-data can deep-link into it). Sample runs are LABELED (banner) and never stashed for
// post-signup seeding — a fictional household must not seed a real user's plan.
export const INSTANT_PREFILL_EVENT = 'mycio:instant-prefill'
export const SAMPLE_SENTENCE =
  "We're 41 and 39 in Texas earning $220k together, $310k saved, saving $2,500 a month — can we retire at 60 spending $80k a year?"

type Cta = {
  tier: string; label: string; first_year: number; renewal: number; installments: number
  first_year_installment: number; renewal_installment: number; why: string[]; upgrade_hint: string; from?: boolean
}
type Chip = { key: string; value: unknown; basis?: string; source?: string; approx?: boolean; detail?: string }
type GoalConfirm = { goal: string; confirm: string; currency?: string; amount_local?: number }
type Result = {
  ok?: boolean; disabled?: boolean; throttled?: boolean; needs_more?: boolean; signup_required?: boolean
  objective?: string; band?: string; goal_probability?: number; after_tax_wealth?: number; currency?: string
  say?: string; ranges?: { text: string; low?: number; mid?: number; high?: number } | null
  driver?: string; headroom?: string; cta?: Cta; tax_hook?: string
  earliest?: { feasible?: boolean; year?: number | null } | null
  goals?: GoalConfirm[]; goal_disclosures?: string[]
  product_analysis?: boolean; direction?: string; assessment?: string; steps?: string[]; plan_adds?: string[]
  explore?: { id: string; label: string }[]
  extracted?: Chip[]; assumptions?: Chip[]; blocks?: { type: string; text?: string }[]; disclaimer?: string
}

const LABELS: Record<string, string> = {
  // w0/wealth is the STARTING balance (not an annual figure); annual_savings is the yearly contribution.
  // Keep them distinct so the starting balance never reads as "savings you add each year".
  current_age: 'age', wealth: 'current savings', w0: 'current savings', income: 'income',
  annual_savings: 'yearly saving', annual_spending: 'retirement spending',
  retire_age: 'retirement age', plan_to_age: 'plan-to age', inflation: 'inflation', jurisdiction: 'location', locale: 'location', country: 'location',
  realization_tax_rate: 'tax when selling to fund a goal', return_drag: 'dividend & interest drag',
  risk_tolerance: 'risk profile',
}
const humanize = (k: string, labels?: Record<string, string>) => labels?.[k] || LABELS[k] || k.replace(/_/g, ' ')
// THE SHARED SYMBOL TABLE (currencyCode.ts): USD alone is "$"; CA$, A$, NZ$, HK$, S$, MX$, NT$ and CN¥ for
// the others; a currency without a symbol (or a minor unit like `GBp`, never upper-cased into pounds) is
// its exact code.
const sym = (ccy?: string) => {
  const code = ccy || 'USD'
  return (exactCurrencyCode(code) && isMajorUnitCode(code) && currencyAffixes(code).prefix) || `${code} `
}
// Percent-form fields (2 means 2%): a bare "inflation: 2" chip read as a mystery number.
const PCT_KEYS = new Set(['inflation', 'realization_tax_rate', 'return_drag'])
// values arrive RAW (full amounts); k/M scaling is applied HERE (display only).
// English writes exactly what it always did; other languages get their own decimal mark, grouping and
// symbol position (numberFormat.ts), with the same distinct symbols.
const fmtVal = (v: unknown, ccy: string | undefined, key: string | undefined, approx: boolean | undefined, locale: string) => {
  if (typeof v !== 'number') return String(v)
  const english = isEnglishNumberLocale(locale)
  // an approximate percent (e.g. an EFFECTIVE tax rate) shows as "≈10%" (rounded) — the exact 10.2% reads as a
  // spuriously precise, arbitrary number; the derivation rides alongside in the chip's `detail`.
  if (key && PCT_KEYS.has(key)) {
    if (english) return approx ? `≈${Math.round(v)}%` : `${v}%`
    return approx ? `≈${formatPercentPoints(v, { maximumFractionDigits: 0, locale })}` : formatPercentPoints(v, { maximumFractionDigits: 3, locale })
  }
  if (v < 1000) return english ? String(v) : formatNumber(v, { maximumFractionDigits: 3, locale })
  const s = sym(ccy)
  if (english) return Math.abs(v) >= 1e6 ? `${s}${(v / 1e6).toFixed(1)}M` : `${s}${(v / 1e3).toLocaleString(locale)}k`
  const scaled = Math.abs(v) >= 1e6
    ? `${formatNumber(v / 1e6, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false, locale })}M`
    : `${formatNumber(v / 1e3, { maximumFractionDigits: 3, locale })}k`
  const code = ccy || 'USD'
  return /^[A-Z]{3}$/.test(code) ? wrapCurrency(scaled, code, locale, s.trim()) : `${scaled} ${code}`
}

// The section's expressive furniture — matches the landing grammar (eyebrow · serif H2 with an italic
// accent · body in the dual-register voice · example chips · three lenses), mirroring the WHAT-IF section.
// All static copy (examples, lenses, the SUGGEST placeholder, labels) lives in the phrase dictionary —
// getMyCIOInstantHeroCopy — so the section follows the site language.
const EYEBROW: React.CSSProperties = { fontSize: 12.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: MC.faint, fontWeight: 600, margin: '0 0 18px' }

export default function InstantHero() {
  const { language, numberLocale: locale } = useMyCIOLanguage()
  const c = getMyCIOInstantHeroCopy(language)
  const rc = getMyCIOReferralShareCopy(language)
  const chips = getMyCIOInstantChips(language)
  const yd = getMyCIOYourDataCopy(language)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [res, setRes] = useState<Result | null>(null)
  // Sample mode: the current result came from the fictional sample household, not the user's own words.
  // Shows the "nothing here is about you" banner and suppresses the post-signup claim stash.
  const [sample, setSample] = useState(false)

  const run = async (given?: string, isSample = false) => {
    const t = (given ?? text).trim()
    if (!t || busy) return
    setSample(isSample)
    setBusy(true)
    setRes(null)
    // zh-Hant (Taiwan/HK) must see Traditional; the backend narrates Simplified. The static catalog is
    // opencc-converted at the getters, but this DYNAMIC computed prose bypasses that — convert on read.
    const localize = (d: Result): Result => (language === 'zh-Hant' ? toTraditionalDeep(d) : d)
    // Funnel step 2 + stash-for-signup, run once on the FINAL computed result (never a fictional SAMPLE run).
    const finalize = (data: Result | null) => {
      if (isSample || !data || !(data.say || data.product_analysis || data.band)) return
      track('instant_run', { jurisdiction: data.currency ?? undefined })
      trackInviteeMilestone('invitee_ran_instant') // no-op unless this session was referred
      try {
        const extract = (data as { _extract?: unknown })._extract
        const result = { objective: data.objective, band: data.band, goal_probability: data.goal_probability,
                         say: data.say, ranges: data.ranges, goals: data.goals, currency: data.currency, driver: data.driver }
        localStorage.setItem('mycio_instant_claim', JSON.stringify({ input: t, extract, result, at: Date.now() }))
      } catch { /* localStorage unavailable — non-fatal */ }
    }
    try {
      const r = await fetch('/api/mycio/assistant/instant', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        // device_id keys the free-run cap to THIS browser (not a shared IP) so demoing to friends isn't walled.
        // stream:true opts into the SSE narration; the backend answers text/event-stream only in agent mode,
        // otherwise it returns JSON and we fall through to the buffered path below.
        body: JSON.stringify({ text: t, lang: language, device_id: getDeviceId(), stream: true }),
      })
      const ctype = r.headers.get('content-type') || ''
      if (r.body && ctype.includes('text/event-stream')) {
        // SSE: narration streams sentence-by-sentence; the final `done` canvas (band/%/chips + gated say)
        // replaces the streamed text. Frame format matches the support chat (event:/data: JSON, blank-line sep).
        const reader = r.body.getReader()
        const decoder = new TextDecoder()
        let buf = '', sayAcc = '', final: Result | null = null
        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          buf += decoder.decode(value, { stream: true })
          const frames = buf.split('\n\n'); buf = frames.pop() || ''
          for (const frame of frames) {
            const evLine = frame.split('\n').find((l) => l.startsWith('event:'))
            const dataLine = frame.split('\n').find((l) => l.startsWith('data:'))
            if (!dataLine) continue
            const ev = evLine ? evLine.slice(6).trim() : 'message'
            let payload: Record<string, unknown> = {}
            try { payload = JSON.parse(dataLine.slice(5).trim()) } catch { continue }
            if (ev === 'token') {
              sayAcc += String(payload.text || '')
              setRes({ ok: true, objective: 'goal_band', say: sayAcc } as Result)   // band fills in at `done`
            } else if (ev === 'done') {
              final = localize(payload as Result)
              setRes(final)
            } else if (ev === 'error') {
              if (!final) setRes({ ok: true, blocks: [{ type: 'text', text: c.errorText }] } as Result)
            }
          }
        }
        finalize(final)
      } else {
        const data = localize((await r.json()) as Result)
        setRes(data)
        finalize(data)
      }
    } catch {
      setRes({ ok: true, blocks: [{ type: 'text', text: c.errorText }] })
    } finally {
      setBusy(false)
    }
  }

  // Prefill hooks for the sample-plan path: the YourDataSection button (same page) dispatches
  // INSTANT_PREFILL_EVENT; /mycio/your-data deep-links with ?try=sample. runRef keeps the mount-only
  // listener pointed at the latest closure (language, busy) without re-subscribing per render.
  const runRef = useRef(run)
  runRef.current = run
  useEffect(() => {
    const onPrefill = (e: Event) => {
      const sentence = (e as CustomEvent<string>).detail || SAMPLE_SENTENCE
      setText(sentence)
      runRef.current(sentence, true)
    }
    window.addEventListener(INSTANT_PREFILL_EVENT, onPrefill)
    try {
      if (new URLSearchParams(window.location.search).get('try') === 'sample') {
        setText(SAMPLE_SENTENCE)
        runRef.current(SAMPLE_SENTENCE, true)
      }
    } catch { /* URL parsing unavailable — no-op */ }
    return () => window.removeEventListener(INSTANT_PREFILL_EVENT, onPrefill)
  }, [])

  const bc = bandColors(res?.band)
  const pct = typeof res?.goal_probability === 'number' ? Math.round(res.goal_probability * 100) : null

  return (
    <div data-testid="instant-hero" className="mc-gap64" style={{ fontFamily: SANS, display: 'flex', gap: 64, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <style>{'@keyframes mcspin{to{transform:rotate(360deg)}}'}</style>
      {/* ---- Left: the pitch, in the landing voice ---- */}
      <div style={{ flex: '1 1 400px', minWidth: 0 }}>
        <p style={EYEBROW} data-reveal>{c.eyebrow}</p>
        <h2 className="mc-h2" style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 'clamp(30px, 7vw, 46px)', lineHeight: 1.08, letterSpacing: '-0.02em', textWrap: 'balance', overflowWrap: 'break-word', margin: '0 0 20px', color: MC.ink }} data-reveal>
          {c.titleBefore}<em style={{ fontStyle: 'italic', color: MC.forest }}>{c.titleEm}</em>{c.titleAfter}
        </h2>
        <p style={{ fontSize: 18, lineHeight: 1.6, color: MC.muted, margin: '0 0 30px', maxWidth: 480, textWrap: 'pretty' }} data-reveal>
          {c.body}
        </p>
        <div data-reveal style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginBottom: 34 }}>
          {c.examples.map((ex) => (
            <button key={ex} type="button" className="mc-chip" data-testid="instant-example"
              onClick={() => { setText(ex); run(ex) }}
              style={{ fontSize: 14.5, color: '#3C382F', background: MC.surface, border: `1px solid ${MC.border}`, borderRadius: 999, padding: '9px 16px', cursor: 'pointer', textAlign: 'left', fontFamily: SANS }}>
              {ex}
            </button>
          ))}
        </div>
        <div data-reveal style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '18px 28px' }}>
          {c.lenses.map((lens) => (
            <div key={lens.t} style={{ borderTop: `2px solid ${MC.forest}`, paddingTop: 12 }}>
              <p style={{ fontSize: 15, fontWeight: 700, color: MC.ink, margin: '0 0 3px' }}>{lens.t}</p>
              <p style={{ fontSize: 14, color: MC.muted2, margin: 0, lineHeight: 1.45 }}>{lens.d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ---- Right: the live computed card (same card treatment as the landing's computed-answer card) ---- */}
      <div data-reveal style={{ flex: '1 1 420px', minWidth: 0 }}>
        <div style={{ background: MC.surface, border: `1px solid ${MC.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 24px 60px -36px rgba(35,33,28,0.4)' }}>
          <div style={{ padding: '22px 26px', borderBottom: '1px solid #ECE5D7' }}>
            <p style={{ fontSize: 11.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: MC.faint, fontWeight: 600, margin: '0 0 10px' }}>{c.inputLabel}</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input aria-label={c.inputLabel} value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); run() } }}
                placeholder={c.placeholder} maxLength={1200}
                style={{ flex: 1, minWidth: 0, fontFamily: SANS, fontSize: 15, color: MC.ink, background: MC.bg, border: `1px solid ${MC.border}`, borderRadius: 999, padding: '13px 18px', outline: 'none' }} />
              <button type="button" onClick={() => run()} disabled={busy || !text.trim()} data-testid="instant-run" className="mc-cta-primary"
                style={{ flex: 'none', fontSize: 15, fontWeight: 600, color: MC.bg, background: MC.forest, border: 'none', borderRadius: 999, padding: '13px 22px', cursor: busy ? 'wait' : 'pointer', opacity: !text.trim() ? 0.6 : 1 }}>
                {busy ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span aria-hidden style={{ width: 13, height: 13, borderRadius: '50%', display: 'inline-block',
                      border: `2px solid ${MC.bg}`, borderTopColor: 'transparent', animation: 'mcspin 0.7s linear infinite' }} />
                    {c.reading}
                  </span>
                ) : c.seeIt}
              </button>
            </div>
          </div>

          <div style={{ padding: '20px 26px 24px' }}>
            <p style={{ fontSize: 11.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: MC.forest, fontWeight: 600, margin: '0 0 14px' }}>{c.computes}</p>

            {/* SAMPLE-HOUSEHOLD banner: whenever the card is showing the fictional sample, say so — clearly,
                before any figure. Clears the moment the user runs their own sentence. */}
            {sample && (
              <p data-testid="instant-sample-banner" style={{ fontSize: 13.5, lineHeight: 1.5, color: '#7A4A22', background: MC.clayBg, border: `1px solid #E0C4AE`, borderRadius: 10, padding: '10px 14px', margin: '0 0 14px' }}>
                {yd.sampleBanner}
              </p>
            )}

            {!busy && !res && (
              // At-rest preview: what the card will hold, so it never reads as an empty box.
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                  <span style={{ fontFamily: SERIF, fontSize: 20, fontWeight: 700, color: MC.faint2 }}>{c.restBand}</span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: MC.faint2 }}>{c.restFunded}</span>
                </div>
                <div style={{ height: 8, borderRadius: 999, background: MC.surfaceAlt }} />
                <p style={{ fontSize: 13.5, color: MC.faint, margin: '14px 0 0', lineHeight: 1.55 }}>
                  {c.restBody}
                </p>
              </div>
            )}

            {busy && !res && <Skeleton c={c} />}

            {res && !res.disabled && (
              <div data-testid="instant-result" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {res.signup_required ? (
                  // Free-run cap reached: the localized note explains it; a signup CTA is the wall.
                  <div data-testid="instant-signup-wall" style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
                    <Note text={res.blocks?.[0]?.text || ''} />
                    <Link href="/sign-in#signup" className="mc-cta-primary"
                      style={{ display: 'inline-block', fontSize: 15.5, fontWeight: 600, color: MC.bg, background: MC.forest, padding: '12px 24px', borderRadius: 999, textDecoration: 'none' }}>
                      {getMyCIOCopy(language).nav.cta}
                    </Link>
                  </div>
                ) : res.throttled ? (
                  <Note text={res.blocks?.[0]?.text || c.throttled} />
                ) : res.needs_more ? (
                  <Note text={res.blocks?.[0]?.text || c.needsMore} />
                ) : res.product_analysis ? (
                  <ProductReveal res={res} c={c} />
                ) : res.say ? (
                  <>
                    {/* STATUS: the score */}
                    <div>
                      <p style={{ fontFamily: SERIF, fontSize: 18, lineHeight: 1.45, color: MC.ink, margin: '0 0 12px' }}>{res.say}</p>
                      {res.objective === 'goal_band' && pct != null && (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                            <span style={{ fontFamily: SERIF, fontSize: 20, fontWeight: 700, color: bc.text }}>{res.band}</span>
                            <span data-testid="instant-pct" style={{ fontSize: 15, fontWeight: 600, color: MC.inkSoft }}>{c.funded(pct)}</span>
                          </div>
                          <div style={{ height: 8, borderRadius: 999, background: MC.surfaceAlt, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: bc.text, borderRadius: 999, transition: 'width 900ms cubic-bezier(0.22,1,0.36,1)' }} />
                          </div>
                        </>
                      )}
                      {res.ranges?.text && <p style={{ fontSize: 13.5, color: MC.muted, margin: '12px 0 0' }}>{res.ranges.text}</p>}
                      {res.driver && <p style={{ fontSize: 13, color: MC.faint, margin: '6px 0 0' }}>{c.biggestFactor(res.driver)}</p>}
                    </div>

                    {/* THE GOAL, read back in the user's own currency ("Assuming a 20% deposit on a £2.0M
                        home (≈ £400k cash) — confirm or adjust?") — what we understood, before the fine print. */}
                    {res.goals?.length ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {res.goals.map((g) => (
                          <p key={g.goal} data-testid="instant-goal-confirm" style={{ fontSize: 14, lineHeight: 1.5, color: MC.ink, margin: 0, padding: '10px 14px', background: MC.bg, borderLeft: `2px solid ${MC.forest}`, borderRadius: '0 10px 10px 0' }}>
                            <strong style={{ color: MC.forest }}>{g.goal}</strong> — {g.confirm}
                          </p>
                        ))}
                      </div>
                    ) : null}

                    {/* the honest fine print: what the year/band is conditional on (savings held, deposit
                        not full price, assumed horizons) — the backend's disclosure notes, verbatim. */}
                    {res.goal_disclosures?.length ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {res.goal_disclosures.map((d, i) => (
                          <p key={i} data-testid="instant-goal-note" style={{ fontSize: 13, lineHeight: 1.55, color: MC.muted, margin: 0, padding: '10px 14px', background: MC.bg, border: `1px solid ${MC.borderSoft}`, borderRadius: 10 }}>{d}</p>
                        ))}
                      </div>
                    ) : null}

                    {res.objective === 'goal_band' && res.band && <ShareBar band={res.band} pct={pct} c={c.share} />}

                    {/* Loop C — problem-template forward: send someone the GENERIC problem-type starter (not
                        the user's private numbers, no dollar amounts). Sits next to the "where you stand" bar. */}
                    <TemplateShareBar problemType={res.goals?.[0]?.goal} c={rc.template} />

                    {/* read + assumed review chips */}
                    {(res.extracted?.length || res.assumptions?.length) ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {res.extracted?.map((c) => (
                          <span key={`e-${c.key}`} data-chip="read" style={chip(MC.forest)}>{humanize(c.key, chips.labels)}: {fmtVal(c.value, res.currency, c.key, undefined, locale)}</span>
                        ))}
                        {res.assumptions?.map((c) => (
                          <span key={`a-${c.key}`} data-chip="assumed" title={c.basis || c.source} style={chip(MC.muted)}>{chips.assumed} {humanize(c.key, chips.labels)}: {fmtVal(c.value, res.currency, c.key, c.approx, locale)}{c.detail ? <span style={{ color: MC.faint }}> ({c.detail})</span> : null}</span>
                        ))}
                      </div>
                    ) : null}

                    {res.tax_hook && <Note text={res.tax_hook} />}
                    {res.headroom && <p style={{ fontSize: 14, lineHeight: 1.5, color: MC.inkSoft, margin: 0 }}>{res.headroom}</p>}

                    {res.explore?.length ? <Explore items={res.explore} title={c.exploreTitle} /> : null}
                    {res.plan_adds?.length ? <PlanAdds items={res.plan_adds} title={c.planAddsTitle} /> : null}
                    {res.cta && <CtaCard cta={res.cta} c={c.cta} />}
                    {res.disclaimer && <p style={{ fontSize: 11.5, color: MC.faint, margin: 0 }}>{res.disclaimer}</p>}
                  </>
                ) : (
                  <Note text={res.blocks?.[0]?.text || c.emptyPrompt} />
                )}
              </div>
            )}
          </div>
        </div>
        <p style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: MC.faint2, margin: '12px 4px 0', letterSpacing: '0.02em' }}>
          {c.micro}
        </p>
      </div>
    </div>
  )
}

function Note({ text }: { text: string }) {
  // Inset on the raised card — cream on paper (the card is MC.surface, so MC.surface here would vanish).
  return <p style={{ fontSize: 15, lineHeight: 1.5, color: MC.ink, background: MC.bg, border: `1px solid ${MC.borderSoft}`, borderRadius: 12, padding: '14px 16px', margin: 0 }}>{text}</p>
}

// Viral share — SANITIZED by default: shares the band/archetype (+ % funded) and a link, with NO dollar amounts
// and a not-advice line. The screenshot-ready artifact that travels; the sensitive numbers stay on the private
// page. Native share where available, clipboard fallback.
function ShareBar({ band, pct, c }: { band: string; pct: number | null; c: MyCIOInstantHeroCopy['share'] }) {
  const [done, setDone] = useState<'' | 'shared' | 'copied'>('')
  const line = c.line(band, pct)
  const text = c.text(line)
  // Value-map loop: the share affordance appeared (funnel numerator for this loop's k-factor).
  useEffect(() => { track('share_prompt_shown', { loop: 'value_map', unit: 'confidence_band' }) }, [])
  // Referral link carries the sender's id + loop so the invitee's landing attributes back (band-only text).
  const shareUrl = buildShareUrl('value_map')
  const share = async () => {
    const channel = await shareOrFallback({ loop: 'value_map', unit: 'confidence_band', title: c.title, text, url: shareUrl })
    if (channel === 'web_share') setDone('shared')
    else if (channel === 'copy_link') setDone('copied')
    if (channel) setTimeout(() => setDone(''), 2500)
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <button type="button" onClick={share} data-testid="instant-share"
        style={{ fontSize: 14, fontWeight: 600, color: MC.forest, background: MC.surface, border: `1px solid ${MC.forest}`, borderRadius: 999, padding: '9px 18px', cursor: 'pointer' }}>
        {done === 'shared' ? c.shared : done === 'copied' ? c.copied : c.cta}
      </button>
      <span style={{ fontSize: 12.5, color: MC.faint }}>{c.note}</span>
    </div>
  )
}

// Loop C — problem-template forward. A second share affordance beside the "where you stand" bar: it offers
// the GENERIC problem-type starter template (a fresh /mycio deep-link), NOT the user's private plan. The text
// is band-free and carries NO dollar amounts — it says "here's the template, mostly set up for your
// situation", nothing about the sender's numbers. Fires share_prompt_shown{template,value_map} when shown.
function TemplateShareBar({ problemType, c }: { problemType?: string; c: MyCIOReferralShareCopy['template'] }) {
  const [done, setDone] = useState<'' | 'shared' | 'copied'>('')
  const problem = (problemType && problemType.trim()) || c.genericProblem
  const text = c.shareText(problem)
  useEffect(() => { track('share_prompt_shown', { loop: 'template', unit: 'value_map' }) }, [])
  const shareUrl = buildShareUrl('template', '/')
  const share = async () => {
    const channel = await shareOrFallback({ loop: 'template', unit: 'value_map', title: c.shareTitle, text, url: shareUrl })
    if (channel === 'web_share') setDone('shared')
    else if (channel === 'copy_link') setDone('copied')
    if (channel) setTimeout(() => setDone(''), 2500)
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13.5, lineHeight: 1.5, color: MC.muted }}>{c.prompt(problem)}</span>
      <button type="button" onClick={share} data-testid="instant-share-template"
        style={{ fontSize: 14, fontWeight: 600, color: MC.forest, background: MC.surface, border: `1px solid ${MC.forest}`, borderRadius: 999, padding: '9px 18px', cursor: 'pointer' }}>
        {done === 'shared' ? c.sent : done === 'copied' ? c.copied : c.cta}
      </button>
      <span style={{ fontSize: 12.5, color: MC.faint }}>{c.note}</span>
    </div>
  )
}

// Staged, moving loading indicator. The /instant request is a single blocking call (LLM parse → jurisdiction
// tax → Monte-Carlo → narrate), so there's no server progress stream — but a static "Reading…" line reads as
// "hung" for the few seconds it runs. This steps a labelled progress bar through the real stages on a timer so
// the wait shows motion. It mounts only while busy (inside Skeleton) and unmounts the moment the result lands.
function StagedProgress({ c }: { c: MyCIOInstantHeroCopy }) {
  const stages = c.skeleton.stages && c.skeleton.stages.length ? c.skeleton.stages : [c.skeleton.reading]
  const [i, setI] = useState(0)
  useEffect(() => {
    // Hold on the final stage (never auto-complete: the result arrival is what ends the wait).
    if (i >= stages.length - 1) return
    const t = setTimeout(() => setI((n) => Math.min(n + 1, stages.length - 1)), 1600)
    return () => clearTimeout(t)
  }, [i, stages.length])
  // Fill proportionally to the stage reached, capped below 100% while still on the last stage (honest: the
  // engine hasn't returned yet — the bar completes by unmounting, not by hitting 100%).
  const pct = Math.min(92, Math.round(((i + 1) / stages.length) * 100))
  return (
    <div style={{ margin: '16px 0 0' }} data-testid="instant-progress">
      <div style={{ height: 4, borderRadius: 999, background: MC.surfaceAlt, overflow: 'hidden', marginBottom: 10 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: MC.forest, borderRadius: 999,
          transition: 'width 1200ms cubic-bezier(0.22,1,0.36,1)' }} />
      </div>
      <p aria-live="polite" style={{ fontSize: 13.5, color: MC.muted, margin: 0, transition: 'opacity 300ms' }}>
        {stages[i]}
      </p>
    </div>
  )
}

function Skeleton({ c }: { c: MyCIOInstantHeroCopy }) {
  // Renders INSIDE the raised card. The staged progress bar is the ONLY loading indicator — no shimmer
  // blocks (redundant motion competing with the bar) and no teaser buttons (those are a post-result
  // "explore further" CTA; showing them before there's a result to explore reads as broken/pre-canned).
  return (
    <div data-testid="instant-skeleton" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <StagedProgress c={c} />
    </div>
  )
}

// INSTANT capability teasers — name the deeper analyses the product can run (downturn stress, retire earlier,
// bequest, allocation) so the user sees the breadth and converts. Zero-compute; each links to sign-up (you sign
// up to run it). This is the conversion driver — keep it prominent, above the "what your plan adds" list.
function Explore({ items, title }: { items: { id: string; label: string }[]; title: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase', color: MC.faint, margin: '2px 0 0' }}>{title}</p>
      {items.map((it) => (
        <a key={it.id} href="/sign-in#signup" data-testid="instant-explore" data-explore={it.id}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: MC.bg, border: `1px solid ${MC.borderSoft}`, borderRadius: 12, padding: '13px 15px', color: MC.ink, textDecoration: 'none', fontSize: 14, lineHeight: 1.35 }}>
          <span>{it.label}</span>
          <span aria-hidden style={{ flex: 'none', color: MC.forest, fontWeight: 700, fontSize: 16 }}>→</span>
        </a>
      ))}
    </div>
  )
}

// "What your full plan adds" — the gated DETAIL, named openly (not a lock) so the gating itself entices the
// upgrade. Shared by the retirement (goal-band / wealth-max) result and the product-fit reveal.
function PlanAdds({ items, title }: { items: string[]; title: string }) {
  return (
    <div style={{ background: MC.bg, border: `1px solid ${MC.forest}`, borderRadius: 14, padding: '16px 20px' }}>
      <p style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase', color: MC.forest, margin: '0 0 10px' }}>{title}</p>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((t, i) => (
          <li key={i} data-testid="instant-plan-add" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, lineHeight: 1.45, color: MC.ink }}>
            <span aria-hidden style={{ flex: 'none', width: 20, height: 20, borderRadius: 999, background: MC.forest, color: MC.bg, fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>+</span>
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// Redacted product-fit reveal (P9 follow-up): a DIRECTIONAL assessment + the method steps MyCIO works through,
// with the exact figures LOCKED behind the paid plan. Shows enough to prove value; hides the numbers to unlock.
function ProductReveal({ res, c }: { res: Result; c: MyCIOInstantHeroCopy }) {
  return (
    <div data-testid="instant-product" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* the directional assessment */}
      <div>
        {res.direction && (
          <span data-testid="instant-direction" style={{ display: 'inline-block', fontSize: 12.5, fontWeight: 700, color: MC.forest, background: MC.pillBg, border: `1px solid ${MC.forest}`, borderRadius: 999, padding: '4px 12px', marginBottom: 10, textTransform: 'capitalize' }}>{res.direction}</span>
        )}
        <p style={{ fontFamily: SERIF, fontSize: 17, lineHeight: 1.55, color: MC.ink, margin: 0 }}>{res.assessment}</p>
      </div>

      {/* method steps — what gets analysed (no numbers) */}
      {res.steps?.length ? (
        <div style={{ background: MC.bg, border: `1px solid ${MC.borderSoft}`, borderRadius: 14, padding: '16px 20px' }}>
          <p style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase', color: MC.faint, margin: '0 0 10px' }}>{c.stepsTitle}</p>
          <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {res.steps.map((s, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, fontSize: 14, lineHeight: 1.45, color: MC.inkSoft }}>
                <span style={{ flex: 'none', width: 20, height: 20, borderRadius: 999, background: MC.surface, border: `1px solid ${MC.border}`, color: MC.forest, fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {res.plan_adds?.length ? <PlanAdds items={res.plan_adds} title={c.planAddsTitle} /> : null}
      {res.cta && <CtaCard cta={res.cta} c={c.cta} />}
      {res.disclaimer && <p style={{ fontSize: 11.5, color: MC.faint, margin: 0 }}>{res.disclaimer}</p>}
    </div>
  )
}

function chip(color: string): React.CSSProperties {
  return { fontSize: 12.5, color, background: MC.bg, border: `1px solid ${MC.borderSoft}`, borderRadius: 999, padding: '5px 11px' }
}

// Monthly-EQUIVALENT framing, with the TOTAL + renewal riding along so the single annual charge is never
// implied to be a recurring monthly one. The old " × 12" installment count was dropped: the charge is one
// annual payment, and where instalments do exist they come from a BNPL provider whose term count is its
// own (Klarna 4, Affirm 3/6/12, …) — never a number we can hardcode here.
function CtaCard({ cta, c }: { cta: Cta; c: MyCIOInstantHeroCopy['cta'] }) {
  return (
    <div data-testid="instant-cta" data-tier={cta.tier} style={{ background: MC.bg, border: `1px solid ${MC.forest}`, borderRadius: 14, padding: '16px 18px' }}>
      <p style={{ fontSize: 14, lineHeight: 1.5, color: MC.ink, margin: '0 0 12px' }}>{cta.upgrade_hint}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <a href={`/sign-in${cta.tier ? `?plan=${encodeURIComponent(cta.tier)}` : ''}#signup`} data-testid="instant-cta-btn"
          style={{ fontSize: 15, fontWeight: 700, color: MC.bg, background: MC.forest, border: 'none', borderRadius: 999, padding: '12px 22px', cursor: 'pointer', textDecoration: 'none', display: 'inline-block' }}>
          {c.build}
        </a>
        <span data-testid="instant-price" lang="en" dir="ltr" style={{ fontSize: 13.5, color: MC.inkSoft }}>
          <strong style={{ color: MC.ink }}>{cta.from ? 'From ' : ''}${cta.first_year.toLocaleString('en-US')} first year</strong>
          {' · '}{cta.from ? 'from ' : ''}about ${cta.first_year_installment}/month equivalent.
          {' '}Renewal {cta.from ? 'from ' : ''}${cta.renewal.toLocaleString('en-US')}/year. Payment options shown at checkout.
        </span>
      </div>
    </div>
  )
}
